import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BarChart3, Plus, Trash2, Save, Pencil, X, Check, FileDown, Link2, Import, CheckCircle2, CircleDashed, FileText, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIAS = [
  { value: 'impuestos', label: 'Impuestos' },
  { value: 'adjudicacion', label: 'Adjudicación' },
  { value: 'juridico', label: 'Jurídico' },
  { value: 'registral', label: 'Registral' },
  { value: 'posesion', label: 'Posesión' },
  { value: 'reforma', label: 'Reforma' },
  { value: 'comercializacion', label: 'Comercialización' },
  { value: 'balance_facturas', label: 'Facturas' },
  { value: 'otros', label: 'Otros' },
];

// Bucket visual config — debe coincidir con la lógica del PDF
// (/assets/{id}/balance-export-pdf) para que la vista web y el PDF
// muestren el mismo orden y agrupación.
const BUCKET_CFG = {
  A: { label: 'Gastos confirmados', bg: 'bg-emerald-50', fg: 'text-emerald-700' },
  B: { label: 'Gastos solo estimados · coste real 0', bg: 'bg-slate-100', fg: 'text-slate-600' },
  C: { label: 'Gastos no confirmados · provisionales', bg: 'bg-amber-100/60', fg: 'text-amber-700' },
};

const _bucketOf = (g) => {
  const realV = Number(g.real || 0);
  if (g.confirmado && realV > 0) return 'A';
  if (realV === 0) return 'B';
  return 'C';
};

/**
 * Devuelve una lista con separadores de sección + filas de datos en el
 * orden A → B → C, coincidiendo con el PDF. Cada entrada es `{_isHeader:
 * true, bucket, count}` o `{bucket, row}`. Si un bucket está vacío se omite.
 */
const buildBalanceRows = (gastos) => {
  const sorted = { A: [], B: [], C: [] };
  for (const g of gastos) sorted[_bucketOf(g)].push(g);
  const out = [];
  for (const b of ['A', 'B', 'C']) {
    if (sorted[b].length === 0) continue;
    out.push({ _isHeader: true, bucket: b, count: sorted[b].length });
    for (const row of sorted[b]) out.push({ bucket: b, row });
  }
  return out;
};

const cur2 = (v) => v != null ? new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(v) : '-';
const cur0 = (v) => v != null ? new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v) : '-';
const pct = (v) => v != null ? `${v > 0 ? '+' : ''}${v.toFixed(2)}%` : '-';
// Format ISO date (YYYY-MM-DD) as Spanish DD/MM/YYYY for display.
const formatDate = (iso) => {
  if (!iso || typeof iso !== 'string') return '-';
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  // Legacy DD/MM/YYYY already → return as-is
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(iso)) return iso;
  return iso;
};

const BalanceTab = ({ asset, assetId, readOnly, canPublish, onAssetUpdated }) => {
  const [gastos, setGastos] = useState([]);
  const [summary, setSummary] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [newForm, setNewForm] = useState({ fecha_gasto: '', concepto: '', categoria: 'otros', estimado: '', real: '' });
  const [importing, setImporting] = useState(false);
  // Operation fields (header form)
  const [opFields, setOpFields] = useState({ fecha_compra: '', fecha_venta: '', valor_salida_real: '' });
  const [savingOp, setSavingOp] = useState(false);
  // PDF export
  const [showExport, setShowExport] = useState(false);
  const [exportOpts, setExportOpts] = useState({ desde_fecha: '', hasta_fecha: '', estimado: true, real: true, desviacion: true, reparto_beneficios: false });
  const [exporting, setExporting] = useState(false);
  // Link invoice
  const [linkingId, setLinkingId] = useState(null);
  // Publish balance
  const [publishMsg, setPublishMsg] = useState('');
  const [showPublishBal, setShowPublishBal] = useState(false);
  const [publishingBal, setPublishingBal] = useState(false);

  const fetchGastos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/assets/${assetId}/balance-gastos`, { withCredentials: true });
      setGastos(res.data);
    } catch {}
  }, [assetId]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/assets/${assetId}/balance-summary`, { withCredentials: true });
      setSummary(res.data);
    } catch {}
  }, [assetId]);

  const fetchDocs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/assets/${assetId}/documents`, { withCredentials: true });
      setDocuments((res.data.items || res.data).filter(d => d.invoice_status === 'confirmado'));
    } catch {}
  }, [assetId]);

  useEffect(() => { fetchGastos(); fetchSummary(); fetchDocs(); }, [fetchGastos, fetchSummary, fetchDocs]);

  // Load operation fields from asset. Depend on asset.id (not the full object) so a
  // refetch after save doesn't clobber values the user is currently typing.
  useEffect(() => {
    if (!asset) return;
    setOpFields({
      fecha_compra: asset.fecha_compra || '',
      fecha_venta: asset.fecha_venta || '',
      valor_salida_real: asset.valor_salida_real ?? '',
    });
  }, [asset?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveOpFields = async (partial) => {
    setSavingOp(true);
    try {
      // Build payload from current opFields + override with explicit partial.
      const merged = { ...opFields, ...(partial || {}) };
      const payload = {
        fecha_compra: merged.fecha_compra === '' ? null : merged.fecha_compra,
        fecha_venta: merged.fecha_venta === '' ? null : merged.fecha_venta,
        valor_salida_real: (merged.valor_salida_real === '' || merged.valor_salida_real == null)
          ? null
          : (parseFloat(merged.valor_salida_real) || null),
      };
      await axios.put(`${API}/assets/${assetId}`, payload, { withCredentials: true });
      fetchSummary();
      // Do NOT call onAssetUpdated here — that re-fetches the parent asset and can race
      // with ongoing user input. Summary is enough to re-render metrics.
      toast.success('Guardado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally { setSavingOp(false); }
  };

  const saveAllOpFields = () => saveOpFields({});

  const importFromRoi = async () => {
    setImporting(true);
    try {
      const res = await axios.post(`${API}/assets/${assetId}/balance-gastos/import-from-roi`, {}, { withCredentials: true });
      if (res.data.imported > 0) {
        toast.success(`Importadas ${res.data.imported} líneas del Análisis ROI`);
        fetchGastos(); fetchSummary();
      } else {
        toast.info('Todas las líneas ya estaban importadas');
      }
    } catch { toast.error('Error al importar'); }
    finally { setImporting(false); }
  };

  const addGasto = async () => {
    if (!newForm.concepto) return;
    try {
      await axios.post(`${API}/assets/${assetId}/balance-gastos`, {
        asset_id: assetId,
        fecha_gasto: newForm.fecha_gasto || null,
        concepto: newForm.concepto,
        categoria: newForm.categoria,
        estimado: parseFloat(newForm.estimado) || 0,
        real: newForm.real !== '' ? parseFloat(newForm.real) : null,
        confirmado: false,
      }, { withCredentials: true });
      setNewForm({ fecha_gasto: '', concepto: '', categoria: 'otros', estimado: '', real: '' });
      fetchGastos(); fetchSummary();
      toast.success('Línea añadida');
    } catch { toast.error('Error'); }
  };

  const startEdit = (g) => {
    setEditingId(g.id);
    setEditForm({
      fecha_gasto: g.fecha_gasto || '',
      concepto: g.concepto,
      categoria: g.categoria,
      estimado: g.estimado,
      real: g.confirmado ? g.real : (g.real !== g.estimado ? g.real : ''),
      confirmado: g.confirmado,
    });
  };

  const saveEdit = async (gid) => {
    try {
      const g = gastos.find(x => x.id === gid);
      await axios.put(`${API}/assets/${assetId}/balance-gastos/${gid}`, {
        asset_id: assetId,
        fecha_gasto: editForm.fecha_gasto || null,
        concepto: editForm.concepto,
        categoria: editForm.categoria,
        estimado: parseFloat(editForm.estimado) || 0,
        real: editForm.real !== '' && editForm.real !== null ? parseFloat(editForm.real) : null,
        confirmado: editForm.confirmado,
        source_document_id: g?.source_document_id || null,
        source_gasto_id: g?.source_gasto_id || null,
      }, { withCredentials: true });
      setEditingId(null);
      fetchGastos(); fetchSummary();
    } catch { toast.error('Error al guardar'); }
  };

  const deleteGasto = async (gid) => {
    try { await axios.delete(`${API}/assets/${assetId}/balance-gastos/${gid}`, { withCredentials: true }); fetchGastos(); fetchSummary(); } catch { toast.error('Error'); }
  };

  const toggleConfirm = async (g) => {
    try {
      await axios.put(`${API}/assets/${assetId}/balance-gastos/${g.id}`, {
        ...g, asset_id: assetId,
        confirmado: !g.confirmado,
        real: !g.confirmado ? (g.real || g.estimado) : g.real,
      }, { withCredentials: true });
      fetchGastos(); fetchSummary();
    } catch { toast.error('Error'); }
  };

  const linkInvoice = async (gastoId, docId) => {
    try {
      await axios.post(`${API}/assets/${assetId}/balance-gastos/${gastoId}/link-invoice/${docId}`, {}, { withCredentials: true });
      toast.success('Factura vinculada');
      setLinkingId(null);
      fetchGastos(); fetchSummary();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const openDocument = async (docId) => {
    try {
      const res = await axios.get(`${API}/documents/${docId}/download`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch { toast.error('Error al abrir documento'); }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const cols = [];
      if (exportOpts.estimado) cols.push('estimado');
      if (exportOpts.real) cols.push('real');
      if (exportOpts.desviacion) cols.push('desviacion');
      const res = await axios.post(`${API}/assets/${assetId}/balance-export-pdf`, {
        desde_fecha: exportOpts.desde_fecha || null,
        hasta_fecha: exportOpts.hasta_fecha || null,
        columnas: cols,
        incluir_reparto_beneficios: exportOpts.reparto_beneficios,
      }, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `balance_${assetId.slice(0,8)}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      toast.success('PDF exportado');
      setShowExport(false);
    } catch { toast.error('Error al exportar'); }
    finally { setExporting(false); }
  };

  const publishBalance = async () => {
    setPublishingBal(true);
    try {
      await axios.post(`${API}/assets/${assetId}/publish-balance`, { mensaje: publishMsg || 'Balance económico actualizado' }, { withCredentials: true });
      toast.success('Balance publicado para inversores');
      setShowPublishBal(false);
      setPublishMsg('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setPublishingBal(false); }
  };

  const totEst = gastos.reduce((s, g) => s + (g.estimado || 0), 0);
  const totReal = gastos.reduce((s, g) => s + (g.real || 0), 0);
  const totDev = totReal - totEst;

  return (
    <div className="space-y-6" data-testid="balance-tab">
      {/* Header */}
      <div className="card-base p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="heading-3 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-blue-600" /> Balance Económico</h3>
          <div className="flex items-center gap-2">
            {!readOnly && (
              <button onClick={importFromRoi} disabled={importing} className="btn-secondary text-sm px-3 py-1.5 flex items-center gap-1" data-testid="import-roi-btn">
                <Import className="w-3.5 h-3.5" /> {importing ? 'Importando...' : 'Importar del ROI'}
              </button>
            )}
            {!readOnly && (
              <button onClick={() => setShowExport(!showExport)} className="btn-secondary text-sm px-3 py-1.5 flex items-center gap-1" data-testid="export-pdf-btn">
                <FileDown className="w-3.5 h-3.5" /> Exportar PDF
              </button>
            )}
            {canPublish && (
              <button onClick={() => setShowPublishBal(!showPublishBal)} className="text-sm px-3 py-1.5 flex items-center gap-1 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors" data-testid="publish-balance-btn">
                <Check className="w-3.5 h-3.5" /> Publicar Balance
              </button>
            )}
          </div>
        </div>
        <p className="text-sm text-slate-500">Control económico real de la operación. Compara gastos estimados vs. reales.</p>
      </div>

      {/* Operation header: fechas + valor salida real */}
      <div className="card-base p-5" data-testid="balance-operation-fields">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-slate-700">Datos de la operación</h4>
          <div className="flex items-center gap-2">
            {summary?.dias_operacion != null && (
              <span className="text-xs font-medium bg-slate-100 text-slate-700 px-2 py-1 rounded" data-testid="dias-operacion-badge">
                {summary.dias_operacion} días de operación
              </span>
            )}
            {!readOnly && (
              <button onClick={saveAllOpFields} disabled={savingOp} className="btn-primary text-xs px-3 py-1" data-testid="op-save-btn">
                {savingOp ? 'Guardando...' : 'Guardar'}
              </button>
            )}
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <label className="text-xs text-slate-500">Fecha de compra</label>
            <input type="date" disabled={readOnly}
              className="input-base text-sm"
              value={opFields.fecha_compra}
              onChange={e => setOpFields(prev => ({ ...prev, fecha_compra: e.target.value }))}
              onBlur={() => saveOpFields({})}
              data-testid="op-fecha-compra" />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-slate-500">Fecha de venta</label>
            <input type="date" disabled={readOnly}
              className="input-base text-sm"
              value={opFields.fecha_venta}
              onChange={e => setOpFields(prev => ({ ...prev, fecha_venta: e.target.value }))}
              onBlur={() => saveOpFields({})}
              data-testid="op-fecha-venta" />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-slate-500">Valor de salida real (€)</label>
            <input type="number" step="100" disabled={readOnly}
              className="input-base text-sm text-right"
              value={opFields.valor_salida_real}
              onChange={e => setOpFields(prev => ({ ...prev, valor_salida_real: e.target.value }))}
              onBlur={() => saveOpFields({})}
              placeholder={asset?.valor_salida_base ? `Est.: ${cur0(asset.valor_salida_base)}` : ''}
              data-testid="op-valor-salida-real" />
          </div>
        </div>
      </div>

      {/* PDF Export Options */}
      {showExport && (
        <div className="card-base p-5 space-y-4 animate-fade-in" data-testid="export-options">
          <h4 className="text-sm font-semibold text-slate-700">Opciones de exportación</h4>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs text-slate-500">Desde fecha</label>
              <input type="date" className="input-base text-sm" value={exportOpts.desde_fecha} onChange={e => setExportOpts({...exportOpts, desde_fecha: e.target.value})} />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-slate-500">Hasta fecha</label>
              <input type="date" className="input-base text-sm" value={exportOpts.hasta_fecha} onChange={e => setExportOpts({...exportOpts, hasta_fecha: e.target.value})} />
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            {[['estimado','Estimados'],['real','Reales'],['desviacion','Desviación']].map(([k,l]) => (
              <label key={k} className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={exportOpts[k]} onChange={e => setExportOpts({...exportOpts, [k]: e.target.checked})} className="w-4 h-4" />
                <span className="text-sm text-slate-700">{l}</span>
              </label>
            ))}
          </div>
          <div className="border-t border-slate-100 pt-3">
            <label className="flex items-start gap-2 cursor-pointer" data-testid="reparto-checkbox-wrapper">
              <input
                type="checkbox"
                checked={exportOpts.reparto_beneficios}
                onChange={e => setExportOpts({...exportOpts, reparto_beneficios: e.target.checked})}
                className="w-4 h-4 mt-0.5"
                data-testid="reparto-checkbox"
              />
              <div>
                <span className="text-sm font-medium text-slate-700">Incluir Reparto de Beneficios</span>
                <p className="text-xs text-slate-500 mt-0.5">Añade una tabla al final del PDF con el reparto del beneficio por gestor/inversor en función de su aportación. Requiere haber registrado participantes con aportación en la ficha del activo.</p>
              </div>
            </label>
          </div>
          <button onClick={exportPdf} disabled={exporting} className="btn-primary text-sm" data-testid="confirm-export-btn">
            {exporting ? 'Generando...' : 'Generar PDF'}
          </button>
        </div>
      )}

      {/* Publish Balance Panel */}
      {showPublishBal && (
        <div className="card-base p-5 space-y-4 animate-fade-in" data-testid="publish-balance-panel">
          <h4 className="text-sm font-semibold text-slate-700">Publicar Balance para inversores</h4>
          <p className="text-xs text-slate-500">Se creará una instantánea del balance actual visible para los inversores asignados.</p>
          <div className="space-y-1">
            <label className="text-xs text-slate-500">Mensaje para inversores</label>
            <textarea className="input-base min-h-[60px]" value={publishMsg} onChange={e => setPublishMsg(e.target.value)} placeholder="Ej: Balance actualizado con gastos reales de reforma..." data-testid="publish-balance-msg" />
          </div>
          <div className="flex gap-2">
            <button onClick={publishBalance} disabled={publishingBal} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50" data-testid="confirm-publish-balance-btn">
              {publishingBal ? 'Publicando...' : 'Publicar Balance'}
            </button>
            <button onClick={() => setShowPublishBal(false)} className="btn-secondary">Cancelar</button>
          </div>
        </div>
      )}

      {/* Investor read-only notice */}
      {readOnly && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
          Último balance publicado (solo lectura)
        </div>
      )}

      {/* Table */}
      <div className="card-base overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="balance-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-3 py-2.5 text-left text-xs font-medium text-slate-500 uppercase w-8"></th>
                <th className="px-3 py-2.5 text-left text-xs font-medium text-slate-500 uppercase w-24">Fecha</th>
                <th className="px-3 py-2.5 text-left text-xs font-medium text-slate-500 uppercase">Concepto</th>
                <th className="px-3 py-2.5 text-left text-xs font-medium text-slate-500 uppercase w-24">Categoría</th>
                <th className="px-3 py-2.5 text-right text-xs font-medium text-slate-500 uppercase w-24">Estimado</th>
                <th className="px-3 py-2.5 text-right text-xs font-medium text-slate-500 uppercase w-24">Real</th>
                <th className="px-3 py-2.5 text-right text-xs font-medium text-slate-500 uppercase w-24">Desviación</th>
                <th className="px-3 py-2.5 text-center text-xs font-medium text-slate-500 uppercase w-20">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {buildBalanceRows(gastos).map(entry => {
                if (entry._isHeader) {
                  const cfg = BUCKET_CFG[entry.bucket];
                  return (
                    <tr key={`hdr-${entry.bucket}`} className={cfg.bg} data-testid={`balance-section-${entry.bucket}`}>
                      <td colSpan={8} className={`px-3 py-2 text-xs font-bold uppercase tracking-wide ${cfg.fg}`}>
                        {cfg.label} ({entry.count})
                      </td>
                    </tr>
                  );
                }
                const g = entry.row;
                const bucket = entry.bucket;
                const FechaIcon = bucket === 'A' ? CheckCircle2 : bucket === 'B' ? XCircle : Clock;
                const iconColor = bucket === 'A' ? 'text-emerald-600' : bucket === 'B' ? 'text-red-500' : 'text-amber-600';
                const rowTone = bucket === 'A' ? 'bg-white' : bucket === 'B' ? 'bg-slate-50/60 italic text-slate-500' : 'bg-amber-50/30 italic';
                return (
                <tr key={g.id} className={`border-b border-slate-100 ${rowTone}`} data-testid={`balance-row-${g.id}`}>
                  {editingId === g.id && !readOnly ? (
                    <>
                      <td className="px-3 py-2"></td>
                      <td className="px-3 py-2"><input type="date" className="input-base text-xs w-full" value={editForm.fecha_gasto} onChange={e => setEditForm({...editForm, fecha_gasto: e.target.value})} /></td>
                      <td className="px-3 py-2"><input className="input-base text-sm w-full" value={editForm.concepto} onChange={e => setEditForm({...editForm, concepto: e.target.value})} /></td>
                      <td className="px-3 py-2"><select className="input-base text-xs w-full" value={editForm.categoria} onChange={e => setEditForm({...editForm, categoria: e.target.value})}>{CATEGORIAS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}</select></td>
                      <td className="px-3 py-2"><input type="number" className="input-base text-sm text-right w-full" value={editForm.estimado} onChange={e => setEditForm({...editForm, estimado: e.target.value})} /></td>
                      <td className="px-3 py-2"><input type="number" className="input-base text-sm text-right w-full" value={editForm.real} onChange={e => setEditForm({...editForm, real: e.target.value})} placeholder="= est." /></td>
                      <td className="px-3 py-2"></td>
                      <td className="px-3 py-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => saveEdit(g.id)} className="text-emerald-600 hover:text-emerald-800 p-0.5"><Save className="w-3.5 h-3.5" /></button>
                          <button onClick={() => setEditingId(null)} className="text-slate-400 hover:text-slate-600 p-0.5"><X className="w-3.5 h-3.5" /></button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-2">
                        {!readOnly ? (
                          <button onClick={() => toggleConfirm(g)} className={`p-0.5 ${g.confirmado ? 'text-emerald-600' : 'text-slate-300 hover:text-slate-500'}`} title={g.confirmado ? 'Confirmado' : 'No confirmado'} data-testid={`confirm-toggle-${g.id}`}>
                            {g.confirmado ? <CheckCircle2 className="w-4 h-4" /> : <CircleDashed className="w-4 h-4" />}
                          </button>
                        ) : (
                          <span className={g.confirmado ? 'text-emerald-600' : 'text-slate-300'}>
                            {g.confirmado ? <CheckCircle2 className="w-4 h-4" /> : <CircleDashed className="w-4 h-4" />}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">
                        <div className="flex items-center gap-1.5">
                          <FechaIcon className={`w-4 h-4 ${iconColor} flex-shrink-0`} />
                          {bucket === 'A' && <span>{formatDate(g.fecha_gasto)}</span>}
                        </div>
                      </td>
                      <td className={`px-3 py-2 ${g.confirmado ? 'font-semibold text-slate-900' : 'text-slate-600'}`}>
                        <div className="flex items-center gap-1.5">
                          <span>{g.concepto}</span>
                          {g.source_document_id && (
                            <button onClick={() => openDocument(g.source_document_id)} className="text-blue-500 hover:text-blue-700 flex-shrink-0" title="Ver factura vinculada" data-testid={`open-doc-${g.id}`}>
                              <FileText className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">{g.categoria}</td>
                      <td className="px-3 py-2 text-right text-slate-700">{cur2(g.estimado)}</td>
                      <td className={`px-3 py-2 text-right ${g.confirmado ? 'font-semibold text-slate-900' : 'text-slate-400 italic'}`}>{cur2(g.real)}{!g.confirmado && g.real === g.estimado ? '' : ''}</td>
                      <td className={`px-3 py-2 text-right ${g.desviacion > 0 ? 'text-red-600' : g.desviacion < 0 ? 'text-emerald-600' : 'text-slate-400'}`}>{g.desviacion !== 0 ? `${g.desviacion > 0 ? '+' : ''}${cur2(g.desviacion)}` : '-'}</td>
                      <td className="px-3 py-2 text-center">
                        {!readOnly && (
                          <>
                            <div className="flex items-center justify-center gap-0.5">
                              <button onClick={() => startEdit(g)} className="text-slate-400 hover:text-blue-600 p-0.5" title="Editar"><Pencil className="w-3.5 h-3.5" /></button>
                              {!g.source_document_id && documents.length > 0 && (
                                <button onClick={() => setLinkingId(linkingId === g.id ? null : g.id)} className="text-slate-400 hover:text-amber-600 p-0.5" title="Vincular factura"><Link2 className="w-3.5 h-3.5" /></button>
                              )}
                              <button onClick={() => deleteGasto(g.id)} className="text-slate-400 hover:text-red-600 p-0.5" title="Eliminar"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                        {linkingId === g.id && (
                          <div className="absolute mt-1 right-4 z-10 bg-white border border-slate-200 rounded-lg shadow-lg p-2 w-64 text-left" data-testid="link-invoice-dropdown">
                            <p className="text-xs font-medium text-slate-600 mb-1">Vincular factura confirmada:</p>
                            {documents.map(d => (
                              <button key={d.id} onClick={() => linkInvoice(g.id, d.id)} className="w-full text-left px-2 py-1.5 text-xs hover:bg-blue-50 rounded flex justify-between items-center">
                                <span className="truncate">{d.nombre}</span>
                                <span className="text-slate-400 flex-shrink-0 ml-1">{cur2(d.invoice_data?.total)}</span>
                              </button>
                            ))}
                            {documents.length === 0 && <p className="text-xs text-slate-400">No hay facturas confirmadas</p>}
                          </div>
                        )}
                          </>
                        )}
                      </td>
                    </>
                  )}
                </tr>
                );
              })}

              {/* Add row */}
              {!readOnly && (
              <tr className="bg-slate-50/50">
                <td className="px-3 py-2"></td>
                <td className="px-3 py-2"><input type="date" className="input-base text-xs w-full" value={newForm.fecha_gasto} onChange={e => setNewForm({...newForm, fecha_gasto: e.target.value})} data-testid="new-balance-fecha" /></td>
                <td className="px-3 py-2"><input className="input-base text-sm w-full" value={newForm.concepto} onChange={e => setNewForm({...newForm, concepto: e.target.value})} placeholder="Concepto" data-testid="new-balance-concepto" /></td>
                <td className="px-3 py-2"><select className="input-base text-xs w-full" value={newForm.categoria} onChange={e => setNewForm({...newForm, categoria: e.target.value})} data-testid="new-balance-categoria">{CATEGORIAS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}</select></td>
                <td className="px-3 py-2"><input type="number" className="input-base text-sm text-right w-full" value={newForm.estimado} onChange={e => setNewForm({...newForm, estimado: e.target.value})} placeholder="0" data-testid="new-balance-estimado" /></td>
                <td className="px-3 py-2"><input type="number" className="input-base text-sm text-right w-full" value={newForm.real} onChange={e => setNewForm({...newForm, real: e.target.value})} placeholder="= est." data-testid="new-balance-real" /></td>
                <td className="px-3 py-2"></td>
                <td className="px-3 py-2 text-center">
                  <button onClick={addGasto} disabled={!newForm.concepto} className="btn-primary px-2 py-1 text-xs disabled:opacity-40" data-testid="add-balance-btn"><Plus className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
              )}

              {/* Totals */}
              <tr className="bg-slate-100 border-t-2 border-slate-300 font-semibold">
                <td className="px-3 py-3" colSpan="4">
                  <span className="text-slate-700">TOTALES</span>
                  <span className="text-xs text-slate-400 ml-2">({gastos.filter(g => g.confirmado).length}/{gastos.length} confirmados)</span>
                </td>
                <td className="px-3 py-3 text-right text-slate-800">{cur2(totEst)}</td>
                <td className="px-3 py-3 text-right text-slate-900">{cur2(totReal)}</td>
                <td className={`px-3 py-3 text-right ${totDev > 0 ? 'text-red-600' : totDev < 0 ? 'text-emerald-600' : 'text-slate-400'}`}>{totDev !== 0 ? `${totDev > 0 ? '+' : ''}${cur2(totDev)}` : '-'}</td>
                <td className="px-3 py-3"></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ROI Metrics */}
      {summary && (
        <div className="card-base p-6" data-testid="balance-metrics">
          <h3 className="heading-3 mb-5">Métricas de Rentabilidad</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Coste Total Estimado</p>
              <p className="text-xl font-bold text-slate-800">{cur0(summary.coste_total_estimado)}</p>
              <p className="text-xs text-slate-400 mt-1">Compra {cur2(summary.precio_compra)}{asset?.precio_compra_es_estimado ? ' *' : ''} + Impuestos {cur2(summary.impuestos)} + Gastos {cur2(summary.total_estimado)}</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Coste Total Real</p>
              <p className="text-xl font-bold text-slate-900">{cur0(summary.coste_total_real)}</p>
              <p className="text-xs text-slate-400 mt-1">Compra {cur2(summary.precio_compra_real || summary.precio_compra)}{asset?.precio_compra_es_estimado && !summary.precio_compra_real ? ' *' : ''} + Impuestos {cur2(summary.impuestos_real || summary.impuestos)} + Gastos {cur2(summary.total_real)}</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div className={`p-4 rounded-lg border ${summary.equity_estimado >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-blue-200'}`}>
              <p className="text-xs uppercase tracking-wide text-blue-600 mb-1">Equity Estimado</p>
              <p className={`text-xl font-bold ${summary.equity_estimado >= 0 ? 'text-blue-700' : 'text-red-700'}`}>{cur0(summary.equity_estimado)}</p>
              {summary.valor_salida != null && summary.valor_salida > 0 && (
                <p className="text-[11px] text-slate-500 mt-0.5">Precio estimado venta: {cur2(summary.valor_salida)}</p>
              )}
              {summary.roi_estimado != null && <p className="text-sm text-blue-600 mt-1">ROI Estimado: {pct(summary.roi_estimado)}</p>}
              {summary.roi_estimado_anualizado != null ? (
                <p className="text-xs text-blue-500 mt-0.5">
                  Anualizado: {pct(summary.roi_estimado_anualizado)}
                  {summary.dias_estimados ? ` (${summary.dias_estimados} días)` : ''}
                </p>
              ) : (
                <p className="text-xs text-slate-400 italic mt-0.5" data-testid="missing-tiempo-hint">
                  Anualizado: rellena <span className="font-semibold">Tiempo estimado (meses)</span> en Análisis ROI
                </p>
              )}
            </div>
            <div className={`p-4 rounded-lg border ${summary.equity_real >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
              <p className="text-xs uppercase tracking-wide text-emerald-600 mb-1">Equity Real</p>
              <p className={`text-xl font-bold ${summary.equity_real >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{cur0(summary.equity_real)}</p>
              {summary.valor_salida_real != null && summary.valor_salida_real > 0 ? (
                <p className="text-[11px] text-slate-500 mt-0.5">Venta real: {cur2(summary.valor_salida_real)}</p>
              ) : (
                <p className="text-[11px] text-slate-400 italic mt-0.5">Usando valor salida estimado</p>
              )}
              {summary.roi_real != null && <p className="text-sm text-emerald-600 mt-1">ROI Real: {pct(summary.roi_real)}</p>}
              {summary.roi_real_anualizado != null && (
                <p className="text-xs text-emerald-500 mt-0.5">
                  Anualizado: {pct(summary.roi_real_anualizado)}
                  {summary.dias_operacion != null
                    ? ` (${summary.dias_operacion} días)`
                    : summary.tiempo_meses > 0 ? ` (${summary.tiempo_meses} meses)` : ''}
                </p>
              )}
            </div>
          </div>

          {/* Mini-indicador: Desviación Real vs Estimado */}
          <DesviacionRealEstimado summary={summary} />

          {summary.total_desviacion !== 0 && (
            <div className={`p-3 rounded-lg border ${summary.total_desviacion > 0 ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
              <p className={`text-sm font-medium ${summary.total_desviacion > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                Desviación total: {summary.total_desviacion > 0 ? '+' : ''}{cur2(summary.total_desviacion)}
                {summary.total_desviacion > 0 ? ' (sobrecostes)' : ' (ahorro)'}
              </p>
            </div>
          )}

          {summary.lineas_confirmadas < summary.lineas_total && (
            <p className="text-xs text-amber-600 mt-3 flex items-center gap-1">
              <CircleDashed className="w-3.5 h-3.5" />
              {summary.lineas_total - summary.lineas_confirmadas} de {summary.lineas_total} líneas sin confirmar. Los datos reales son provisionales.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default BalanceTab;

// Mini-indicador: desviación del real vs. estimado (días y ROI).
// Sólo se muestra si la operación tiene datos reales (fechas completas y/o ROI real calculado).
const DesviacionRealEstimado = ({ summary }) => {
  if (!summary) return null;
  const diasReal = summary.dias_operacion;
  const diasEst  = summary.dias_estimados;
  const roiReal  = summary.roi_real;
  const roiEst   = summary.roi_estimado;
  const roiAnualReal = summary.roi_real_anualizado;
  const roiAnualEst  = summary.roi_estimado_anualizado;

  const hasDias  = diasReal != null && diasEst != null;
  const hasRoi   = roiReal != null && roiEst != null;
  const hasAnual = roiAnualReal != null && roiAnualEst != null;
  // Show this block always — we guide the user to fill missing inputs instead of hiding.

  const pctSigned = (v) => v == null ? '-' : `${v > 0 ? '+' : ''}${v.toFixed(2)}%`;

  // "Positivo" = bueno para el inversor:
  //   - días: menos días → más rápido → delta negativo → verde
  //   - ROI: más ROI → delta positivo → verde
  //   - ROI anualizado: más → verde
  // Clases estáticas (no interpoladas) para que Tailwind JIT las incluya sin fallos.
  const GREEN = 'text-emerald-700';
  const RED = 'text-red-600';
  const NEUTRAL = 'text-slate-500';

  const diasDelta = hasDias ? diasReal - diasEst : null;
  const roiDelta  = hasRoi  ? roiReal - roiEst   : null;
  const anualDelta = hasAnual ? roiAnualReal - roiAnualEst : null;

  // Para días: delta NEGATIVO (menos días) es bueno → verde.
  const classDias  = diasDelta == null ? NEUTRAL : diasDelta < 0 ? GREEN : diasDelta > 0 ? RED : NEUTRAL;
  // Para ROI y ROI anual: delta POSITIVO es bueno → verde.
  const classRoi   = roiDelta == null  ? NEUTRAL : roiDelta > 0  ? GREEN : roiDelta < 0  ? RED : NEUTRAL;
  const classAnual = anualDelta == null ? NEUTRAL : anualDelta > 0 ? GREEN : anualDelta < 0 ? RED : NEUTRAL;

  return (
    <div className="p-4 mb-4 rounded-lg border border-slate-200 bg-white" data-testid="desviacion-real-estimado">
      <p className="text-xs uppercase tracking-wide text-slate-500 mb-3">Desviación Real vs Estimado</p>
      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <p className="text-xs text-slate-500">Días de ejecución</p>
          {hasDias ? (
            <>
              <p className={`text-lg font-semibold ${classDias}`} data-testid="desv-dias">
                {diasDelta > 0 ? '+' : ''}{diasDelta} días
              </p>
              <p className="text-[11px] text-slate-400">{diasReal} real · {diasEst} estimado</p>
            </>
          ) : (
            <>
              <p className="text-lg font-semibold text-slate-300">-</p>
              <p className="text-[11px] text-slate-400 italic">
                {diasReal == null ? 'Falta fecha compra/venta' : 'Falta tiempo estimado'}
              </p>
            </>
          )}
        </div>
        <div>
          <p className="text-xs text-slate-500">ROI</p>
          {hasRoi ? (
            <>
              <p className={`text-lg font-semibold ${classRoi}`} data-testid="desv-roi">{pctSigned(roiDelta)}</p>
              <p className="text-[11px] text-slate-400">{roiReal.toFixed(2)}% real · {roiEst.toFixed(2)}% estimado</p>
            </>
          ) : (
            <>
              <p className="text-lg font-semibold text-slate-300">-</p>
              <p className="text-[11px] text-slate-400 italic">
                {roiReal == null ? 'Sin gastos reales confirmados' : 'Faltan datos estimados'}
              </p>
            </>
          )}
        </div>
        <div>
          <p className="text-xs text-slate-500">ROI anualizado</p>
          {hasAnual ? (
            <>
              <p className={`text-lg font-semibold ${classAnual}`} data-testid="desv-roi-anual">{pctSigned(anualDelta)}</p>
              <p className="text-[11px] text-slate-400">{roiAnualReal.toFixed(2)}% real · {roiAnualEst.toFixed(2)}% estimado</p>
            </>
          ) : (
            <>
              <p className="text-lg font-semibold text-slate-300">-</p>
              <p className="text-[11px] text-slate-400 italic">Falta tiempo estimado (Análisis ROI)</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
